// Service for communicating with OpenRouter API via Netlify function proxy

// The backend URL for our Netlify function
const OPENROUTER_FUNCTION_URL = '/.netlify/functions/openrouter-proxy';

/**
 * Sends a prompt to an LLM via OpenRouter
 * @param {string} prompt - The prompt text to send
 * @param {string} model - The model identifier (e.g., 'anthropic/claude-3.5-sonnet')
 * @param {Array} [imageUrls] - Optional array of image URLs to include (for multimodal models)
 * @returns {Promise<Object>} - The parsed response from the LLM
 */
export async function sendPromptToLLM(prompt, model = 'anthropic/claude-3.5-sonnet', imageUrls = []) {
  try {
    // Construct the messages array
    const messages = [];
    
    // Create content array for the user message
    const content = [];
    
    // Add the text content
    content.push({
      type: 'text',
      text: prompt
    });
    
    // Add image URLs if they exist (only for models that support images)
    if (imageUrls && imageUrls.length > 0) {
      for (const url of imageUrls) {
        content.push({
          type: 'image_url',
          image_url: {
            url: url
          }
        });
      }
    }
    
    // Add the user message with content
    messages.push({
      role: 'user',
      content: content.length === 1 ? content[0].text : content
    });
    
    // Prepare the request payload
    const payload = {
      model,
      messages
    };
    
    // Make the request to our Netlify function
    const response = await fetch(OPENROUTER_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      throw new Error(`OpenRouter request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[openrouter-api] Error:', error);
    throw error;
  }
}

/**
 * Extracts and attempts to parse JSON content from an OpenRouter response
 * @param {Object} response - The response object from OpenRouter
 * @returns {Object|string} - Parsed JSON or the raw string if parsing fails
 */
export function extractContentFromResponse(response) {
  try {
    if (!response || !response.choices || !response.choices[0] || !response.choices[0].message) {
      throw new Error('Invalid OpenRouter response format');
    }
    
    const content = response.choices[0].message.content;
    
    // Try to parse as JSON
    try {
      return JSON.parse(content);
    } catch (parseError) {
      // If not valid JSON, return as string
      console.warn('[openrouter-api] Response is not valid JSON:', content);
      return content;
    }
  } catch (error) {
    console.error('[openrouter-api] Error extracting content:', error);
    throw error;
  }
}
